import React, { forwardRef, useMemo } from 'react';
import { Avatar, type AvatarProps } from 'antd';
import classNames from 'classnames';

import s from './styles.module.scss';
import { getColorTheme } from './utils';

export interface UserAvatarProps {
  firstName?: string;
  lastName?: string;
  size?: AvatarProps['size'];
  className?: string;
  avatarClassName?: string;
  onClick?: () => void;
  dataCy?: string;
  src?: string;
  alt?: string;
  icon?: React.ReactNode;
  colorOverride?: { bg: string; color: string };
}

const UserAvatar = forwardRef<HTMLButtonElement, UserAvatarProps>(
  (
    {
      firstName,
      lastName,
      size = 36,
      className,
      avatarClassName,
      onClick,
      dataCy,
      src,
      alt,
      icon,
      colorOverride,
    },
    ref,
  ) => {
    const hasCustomVisual = Boolean(src || icon);
    const initials = useMemo(() => {
      if (!src && !icon && (firstName || lastName)) {
        return `${firstName?.[0]?.toUpperCase() || ''}${
          lastName?.[0]?.toUpperCase() || ''
        }`;
      }
      return undefined;
    }, [firstName, lastName, icon, src]);

    const colorTheme = useMemo(() => {
      if (hasCustomVisual || !initials) return undefined;
      const seed = `${firstName ?? ''}${lastName ?? ''}`.trim();
      return getColorTheme(seed || initials);
    }, [firstName, lastName, hasCustomVisual, initials]);

    const avatarStyle = useMemo(() => {
      if (colorOverride)
        return {
          backgroundColor: colorOverride.bg,
          color: colorOverride.color,
        };
      if (colorTheme)
        return { backgroundColor: colorTheme.bg, color: colorTheme.color };
      return undefined;
    }, [colorOverride, colorTheme]);

    return (
      <button
        ref={ref}
        data-cy={dataCy}
        className={classNames(s.avatar_wrapper, className)}
        onClick={onClick}
      >
        <Avatar
          size={size}
          className={classNames(s.avatar, avatarClassName)}
          src={src}
          alt={alt}
          icon={icon}
          style={avatarStyle}
        >
          {initials || 'U'}
        </Avatar>
      </button>
    );
  },
);

UserAvatar.displayName = 'UserAvatar';

export default UserAvatar;
